import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  Typography,
} from "@mui/material";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import LockIcon from "@mui/icons-material/Lock";
import { loadStripe } from "@stripe/stripe-js";
import {
  CardElement,
  Elements,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PROMISE);

const BRAND = {
  redDeep: "#7A0E0E",
  red: "#B71C1C",
  amber: "#FFB300",
  cream: "#FFF8F0",
  creamDeep: "#FBE9D2",
  charcoal: "#2A1810",
  muted: "#8B6F60",
  gradPrimary:
    "linear-gradient(135deg, #7A0E0E 0%, #B71C1C 45%, #E64A19 100%)",
};

const cardStyle = {
  style: {
    base: {
      fontSize: "16px",
      color: "#2A1810",
      "::placeholder": {
        color: "#8B6F60",
      },
    },
    invalid: {
      color: "#B71C1C",
    },
  },
};

const StripePaymentForm = ({
  amount,
  currency,
  clientSecret,
  paymentIntentId,
  loadingIntent,
  intentError,
  onClose,
  onPaymentSuccess,
}) => {
  const stripe = useStripe();
  const elements = useElements();

  const [processing, setProcessing] = useState(false);
  const [cardReady, setCardReady] = useState(false);
  const [cardError, setCardError] = useState("");

  const handlePay = async () => {
    if (!stripe || !elements) {
      setCardError("Stripe is still loading. Please try again.");
      return;
    }

    if (!clientSecret || !paymentIntentId) {
      setCardError("Payment could not be initialized.");
      return;
    }

    const cardElement = elements.getElement(CardElement);

    if (!cardElement) {
      setCardError("Card input is not ready.");
      return;
    }

    try {
      setProcessing(true);
      setCardError("");

      const { error, paymentIntent } = await stripe.confirmCardPayment(
        clientSecret,
        {
          payment_method: {
            card: cardElement,
          },
        }
      );

      if (error) {
        setCardError(error.message || "Payment failed. Please try again.");
        return;
      }

      if (paymentIntent?.status !== "succeeded") {
        setCardError(
          `Payment status is ${paymentIntent?.status || "unknown"}. Please try again.`
        );
        return;
      }

      onPaymentSuccess({
        payment_intent_id: paymentIntentId,
        stripe_payment_intent: paymentIntent,
      });
    } catch (error) {
      setCardError(error?.message || "Payment failed. Please try again.");
    } finally {
      setProcessing(false);
    }
  };

  return (
    <>
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box
            sx={{
              width: 42,
              height: 42,
              borderRadius: "50%",
              background: BRAND.gradPrimary,
              display: "grid",
              placeItems: "center",
              color: "#fff",
            }}
          >
            <CreditCardIcon />
          </Box>

          <Box>
            <Typography sx={{ fontWeight: 900, color: BRAND.charcoal }}>
              Secure Card Payment
            </Typography>
            <Typography sx={{ fontSize: 13, color: BRAND.muted }}>
              Enter your card details to confirm this order.
            </Typography>
          </Box>
        </Stack>
      </DialogTitle>

      <DialogContent>
        <Stack spacing={2}>
          <Alert
            severity="info"
            icon={<LockIcon />}
            sx={{ borderRadius: 3, fontWeight: 600 }}
          >
            Your card details are securely handled by Stripe.
          </Alert>

          <Box
            sx={{
              p: 2,
              borderRadius: 3,
              background: BRAND.cream,
              border: "1px solid rgba(122,14,14,0.12)",
            }}
          >
            <Typography sx={{ fontSize: 13, color: BRAND.muted }}>
              Amount to Pay
            </Typography>
            <Typography
              sx={{
                fontWeight: 900,
                fontSize: "1.5rem",
                color: BRAND.redDeep,
              }}
            >
              {currency?.toUpperCase() || "INR"} {amount || "0.00"}
            </Typography>
          </Box>

          <Divider />

          {loadingIntent ? (
            <Stack alignItems="center" spacing={1.5} sx={{ py: 3 }}>
              <CircularProgress sx={{ color: BRAND.redDeep }} />
              <Typography sx={{ color: BRAND.muted, fontWeight: 600 }}>
                Preparing secure payment...
              </Typography>
            </Stack>
          ) : intentError ? (
            <Alert severity="error" sx={{ borderRadius: 3 }}>
              {intentError}
            </Alert>
          ) : (
            <Box
              sx={{
                p: 2,
                borderRadius: 3,
                bgcolor: "#fff",
                border: cardError
                  ? `1px solid ${BRAND.red}`
                  : "1px solid rgba(122,14,14,0.22)",
              }}
            >
              <CardElement
                options={cardStyle}
                onReady={() => setCardReady(true)}
                onChange={(event) => {
                  setCardError(event.error?.message || "");
                }}
              />
            </Box>
          )}

          {cardError && (
            <Alert severity="error" sx={{ borderRadius: 3 }}>
              {cardError}
            </Alert>
          )}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={onClose}
          disabled={processing}
          sx={{
            borderRadius: 999,
            color: BRAND.redDeep,
            fontWeight: 800,
          }}
        >
          Cancel
        </Button>

        <Button
          variant="contained"
          onClick={handlePay}
          disabled={
            loadingIntent ||
            Boolean(intentError) ||
            processing ||
            !stripe ||
            !cardReady
          }
          sx={{
            borderRadius: 999,
            px: 3,
            fontWeight: 900,
            background: BRAND.gradPrimary,
          }}
        >
          {processing ? "Processing..." : "Pay & Place Order"}
        </Button>
      </DialogActions>
    </>
  );
};

const StripePaymentDialog = ({
  open,
  onClose,
  amount,
  currency,
  clientSecret,
  paymentIntentId,
  loadingIntent,
  intentError,
  onPaymentSuccess,
}) => {
  const options = useMemo(() => {
    if (!clientSecret) return undefined;
    return { clientSecret };
  }, [clientSecret]);

  return (
    <Dialog
      open={open}
      onClose={loadingIntent ? undefined : onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          borderRadius: 4,
          overflow: "hidden",
        },
      }}
    >
      <Elements stripe={stripePromise} options={options}>
        <StripePaymentForm
          amount={amount}
          currency={currency}
          clientSecret={clientSecret}
          paymentIntentId={paymentIntentId}
          loadingIntent={loadingIntent}
          intentError={intentError}
          onClose={onClose}
          onPaymentSuccess={onPaymentSuccess}
        />
      </Elements>
    </Dialog>
  );
};

export default StripePaymentDialog;